/**
 *
 *  Series of Events details page
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 26/08/2025.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Title, Meta } from "react-meta-elements";

import { useAppState } from "../../state";
import { useRegion } from "../../context/RegionProvider";

import { useGetSeriesById } from "../../hooks/useEvents/useEvents";

// Components
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import ScrollToTopOnMount from "../ScrollToTopOnMount/ScrollToTopOnMount";
import Button from "../Button/Button";
import BreadcrumbNav from "../BreadcrumbNav/BreadcrumbNav";
import { MarkdownRenderer } from "../MarkdownRenderer/MarkdownRenderer";
import TitleWithSeparator from "../TitleWithSeparator/TitleWithSeparator";
import EventCard from "../LifestyleLP/EventCard/EventCard";

// Hooks
import useTheme from "../ThemeProvider/useTheme/useTheme";

// Utils

// Icons
import CheckNiceIcon from "../../icons/CheckboxOIcon";
import ArrowLeftIcon from "../../icons/ArrowLeftIcon";
import ArrowRightIcon from "../../icons/ArrowRightIcon";
import { BreadcrumbsSkeleton } from "../BreadcrumbsSkeleton/BreadcrumbsSkeleton";
import { SeriesDetailPageLoading } from "./SeriesDetailPageLoading/SeriesDetailPageLoading";

// Images

export function SeriesDetailPage() {
  const { SeriesId } = useParams();

  const { theme } = useTheme();
  const { region, city } = useRegion();

  const { user } = useAppState();

  const { data, isLoading } = useGetSeriesById(SeriesId, user && user.id);

  const heroRef = React.useRef(null);
  const sliderRef = React.useRef(null);
  const isUserLoggedIn = !!(user && user.id);
  const seriesData = data && data.data;

  var formattedDescription = "";

  if (isLoading) {
    return (
      <>
        <ScrollToTopOnMount />
        <Meta
          name="description"
          content={`On-demand, in person, personal training in Los Angeles`}
        />
        <Header
          behaviour="none"
          isLoggedIn={isUserLoggedIn}
          heroRef={heroRef}
        />
        <div />
        <BreadcrumbsSkeleton />
        <SeriesDetailPageLoading />
      </>
    );
  }

  return (
    <>
      <ScrollToTopOnMount />
      {/*<Title>Personal Training in London | TruBe</Title>*/}
      <Meta
        name="description"
        content={`On-demand, in person, personal training in Los Angeles`}
      />
      <Header
        showRegionSelect={false}
        behaviour="none"
        isLoggedIn={isUserLoggedIn}
        heroRef={heroRef}
      />

      <div />

      <div className="tbk-overflow-hidden">
        <div className="tbk-container tbk-mx-auto tbk-px-2 lg:tbk-px-8">
          <div
            ref={sliderRef}
            className="tb-slider tbk-mb-3 tbk-mt-3 tbk-flex tbk-flex-nowrap tbk-items-center"
          >
            <BreadcrumbNav to={"/lifestyle"}>
              Lifestyle collective
            </BreadcrumbNav>

            <ArrowRightIcon className="tbk-text-blue-grey-light" />

            <BreadcrumbNav to={"/events/series"}>
              Series of Classes
            </BreadcrumbNav>

            <ArrowRightIcon className="tbk-text-blue-grey-light" />

            <BreadcrumbNav>{seriesData && seriesData.title}</BreadcrumbNav>
          </div>
        </div>
      </div>

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pb-4 lg:tbk-pb-4 lg:tbk-pt-4">
        <div className="tbk-container tbk-px-2 lg:tbk-px-8">
          <div className="tbk-grid tbk-gap-4 tbk-pb-4 lg:tbk-grid-cols-2 lg:tbk-gap-4">
            <div className="tbk--order-1_ lg:tbk-order-1_ tbk-relative tbk--ml-2 tbk--mr-2 tbk-overflow-hidden tbk-bg-home-our-coaches-mobile lg:tbk-ml-0 lg:tbk-mr-0 lg:tbk-h-auto lg:tbk-bg-none">
              <div className="tbk-relative">
                <img
                  className="tbk-w-full tbk-object-cover lg:tbk-rounded-2xl"
                  src={seriesData && seriesData.coverPhoto}
                  alt={seriesData && seriesData.title}
                />
              </div>
            </div>

            <div className="7 tbk-mb-2 lg:tbk-mb-0">
              <div className="tbk-mb-4_ _tbk-flex tbk-p-1_ tbk-relative tbk-items-center">
                {seriesData && seriesData.city !== "ALL" ? (
                  <div
                    className={`tbk-text-h3-subtitle tbk-mb-0.5 !tbk-leading-unified ${
                      isLoading ? "tbk-text-blue-grey-light" : "tbk-text-grey"
                    } lg:tbk-mb-1`}
                  >
                    {isLoading ? "City" : adaptCityName(seriesData)}
                  </div>
                ) : null}

                <div className="tbk-min-w-0 tbk-grow tbk-overflow-hidden">
                  <h1
                    className={`tbk-text-title-bold lg:tbk-text-h2-black 0.5 tbk-mb-2 tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap tbk-text-left tbk-text-primary`}
                  >
                    {seriesData && seriesData.title}
                  </h1>
                  <MarkdownRenderer
                    markdown={seriesData && seriesData.description}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* The Classes */}
          {seriesData && seriesData.events.length ? (
            <div>
              <TitleWithSeparator
                textAlign="left"
                className="tbk-mb-3 tbk-pt-4 lg:tbk-mb-3"
                titleClass="tbk-text-center tbk-bg-grey-light_ tbk-text-blue-grey tbk-text-h3-subtitle lg:tbk-text-title-bold lg:!tbk-font-semibold"
                separatorClass="tbk-border-blue-grey-light"
              >
                The Classes
              </TitleWithSeparator>

              <div className="tbk-mb-4 tbk-grid tbk-gap-2 md:tbk-grid-cols-2 lg:tbk-grid-cols-3">
                {seriesData.events.map((evt, index) => (
                  <EventCard
                    key={index}
                    href={`/events/${evt.id}`}
                    imgUrl={evt.coverPhoto}
                    title={evt.name}
                    startDate={evt.start}
                    endDate={evt.finish}
                    place={evt.location.name}
                    joined={evt.joined}
                    soldOut={evt.capacity === evt.currentCapacity}
                    status={evt.status}
                    region={region}
                    timezone={evt.timezone}
                    // onClick={handleEventCardClick}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="tbk-pt-4_ lg:tbk-pt-4_ 8 tbk-pb-7 lg:tbk-pb-8">
        <div className="tbk-container tbk-mx-auto tbk-px-2 lg:tbk-px-8">
          <Link to={"/events/series"}>
            <Button
              className="tbk-mt-4_ tbk-mx-auto tbk-w-32 tbk-text-primary"
              variant="secondary"
              size={"md"}
              startIcon={<ArrowLeftIcon className="tbk-tex-primary" />}
            >
              Back
            </Button>
          </Link>
        </div>
      </div>

      <Footer regionSelect={false} />
    </>
  );
}

const adaptCityName = (data) => {
  if (!data || !data.events || !data.events.length) {
    return data;
  }

  const cityName = data && data.city; //events[0].city;

  return cityName
    ? cityName === "LOS_ANGELES"
      ? "Los Angeles"
      : cityName === "SAN_DIEGO"
      ? "San Diego"
      : cityName === "NEW_YORK"
      ? "New York"
      : cityName === "MIAMI"
      ? "Miami"
      : cityName === "LONDON"
      ? "London"
      : "All"
    : "All";
};
